import { useCallback, useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Alert, Button, Checkbox, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { Text } from "ui/Typography"
import { spacings } from "themes/variables"
import { routes } from "router"
import API from "constants/api"
import { isEmbeddedContext } from "helpers/embedded"
import { numberToCurrency } from "helpers/string"
import {
  clearAttemptRef,
  getPowerTranzErrorCode,
  getPowerTranzErrorMessage,
  getSavedAttemptRef,
  isConfirmedAttemptState,
  isFailedAttemptState,
  saveAttemptRef,
  saveConfirmationPath,
  toCurrencyCode
} from "helpers/powertranz"
import { createPowerTranzPayment, getPowerTranzPaymentStatus } from "store/payment-provider"
import { mapPowerTranzPaymentSession, mapPowerTranzPaymentStatus } from "store/powertranzApi"
import {
  CONFIRMING_STATES,
  ERROR_CODES,
  FINAL_CHECKOUT_ERROR_CODES,
  HOSTED_PAGE_IFRAME_SANDBOX,
  HOSTED_PAGE_POPUP_FEATURES,
  HOSTED_PAGE_POPUP_NAME,
  HOSTED_PAGE_SESSION_SECONDS,
  HOSTED_PAGE_STATES,
  PAYMENT_PROVIDERS,
  PENDING_ERROR_CODES,
  POPUP_CLOSE_ATTEMPTS,
  POPUP_CLOSED_CHECK_MS,
  RESULT_MESSAGE_TYPE
} from "constants/powertranz"
import { formatCountdown, secondsUntil, useExpiryCountdown } from "./useExpiryCountdown"
import styles from "./index.module.css"

const API_ORIGIN = API.API_URL ? new URL(API.API_URL).origin : null
// The guest closed the payment window before paying (local state, not an API code).
const POPUP_CLOSED = "popup_closed"

// A close() sent while the popup is still moving from PowerTranz to the API's result page is dropped
// by the browser (seen in Chrome), so keep asking until it has gone. Closing twice is harmless.
const closePopup = (popup) => {
  if (!popup || popup.closed) return
  popup.close()
  let attempts = 1
  const timer = setInterval(() => {
    if (popup.closed || attempts >= POPUP_CLOSE_ATTEMPTS) {
      clearInterval(timer)
      return
    }
    attempts += 1
    popup.close()
  }, POPUP_CLOSED_CHECK_MS)
}

const writeHostedPage = (targetWindow, redirectData) => {
  targetWindow.document.open()
  targetWindow.document.write(redirectData)
  targetWindow.document.close()
}

// Paid or still being confirmed → the result page, whatever the timer or popup says.
const isSettledOrConfirming = (status) => isConfirmedAttemptState(status?.state) || CONFIRMING_STATES.includes(status?.state)

// Starts a PowerTranz hosted-page checkout on the booking guide's merchant account. Card entry,
// 3DS and fraud screening all happen on PowerTranz's page; the API confirms the charge server to
// server and sends the browser to the result page, which asks the API for the real outcome.
const PowerTranzCheckout = ({
  bookingId,
  promoCodes = [],
  linkToken,
  saveCardOffer,
  isDisabled = false,
  onSessionChange,
  onResult,
  isStayingOnPage = false,
  confirmationPath
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isCreating = useSelector((state) => state.paymentProvider.createPowerTranzPayment.loading)
  // The widget on a guide's own site can't frame the hosted page (no frame-ancestors entry for
  // guide domains — spec DS-06), so it opens in a popup instead. A page that stays put (the guide's
  // booking form) uses the popup too: a top-level window needs no frame-ancestors entry.
  const isPopupMode = isStayingOnPage || isEmbeddedContext()
  // A payment link carries its booking in the signed token, so the saved attempt is kept per link.
  const storageKey = bookingId || (linkToken ? `link_${linkToken.slice(-32)}` : null)
  const popupRef = useRef(null)
  const iframeRef = useRef(null)

  // The API's result page sends its opener to the result URL. Cut that link so it shows the result
  // in the popup instead, and this page reads the outcome from the status endpoint.
  const writeToPopup = (popup, redirectData) => {
    if (isStayingOnPage) popup.opener = null
    writeHostedPage(popup, redirectData)
  }
  const [session, setSession] = useState(null)
  const [isSaveCardConsented, setIsSaveCardConsented] = useState(false)
  // An API error code, or "popup_blocked"; null when there's nothing to show.
  const [errorCode, setErrorCode] = useState(null)
  // Messages the API returned for the promo codes (invalid_promo_code).
  const [promoErrors, setPromoErrors] = useState([])
  // payment_in_progress: another tab or an earlier attempt holds the open checkout until this time.
  const [inProgressUntil, setInProgressUntil] = useState(null)
  // The attempt whose expiry the API has already been asked about.
  const [expiryCheckedFor, setExpiryCheckedFor] = useState(null)
  const [isPopupClosed, setIsPopupClosed] = useState(false)

  const sessionSecondsLeft = useExpiryCountdown(session?.expiresAt)
  const inProgressSecondsLeft = useExpiryCountdown(inProgressUntil)
  const isSessionLive = !!session && sessionSecondsLeft > 0
  const isSessionExpired = !!session && sessionSecondsLeft === 0
  const attemptRef = session?.attemptRef
  // The countdown reached 0: the hosted page stays up (derived here, so it never unmounts and
  // restarts) until the API says whether the guest paid.
  const isCheckingExpiry = isSessionExpired && !!attemptRef && expiryCheckedFor !== attemptRef
  const isHostedPageShown = isSessionLive || isCheckingExpiry
  // The embedded widget reads the popup's outcome itself; a caller with onResult polls on its own.
  const isWatchingPopup = isPopupMode && !onResult

  useEffect(() => () => closePopup(popupRef.current), [])

  // Lets the booking wizard lock "Previous" while the hosted page is open (integration guide 5.3).
  useEffect(() => {
    onSessionChange?.(isHostedPageShown)
  }, [isHostedPageShown, onSessionChange])

  // A caller with its own result view (the guide's booking form) is handed the attempt instead.
  const goToResult = useCallback(
    (attemptRef) => {
      if (onResult) {
        onResult(attemptRef)
        return
      }
      navigate(
        routes.paymentSuccess(
          {},
          {
            ...(bookingId && { booking_id: bookingId }),
            provider: PAYMENT_PROVIDERS.POWERTRANZ,
            ...(attemptRef && { attempt_ref: attemptRef })
          }
        )
      )
    },
    [bookingId, navigate, onResult]
  )

  // Where an earlier attempt for this booking stands (reload, second tab, payment_in_progress):
  // confirmed or still being confirmed → the result page, never the Pay button; still on its
  // hosted page → wait for it to expire (its page can't be reopened); finished → forget it.
  const resolveExistingAttempt = useCallback(
    async (attemptRef) => {
      try {
        const status = mapPowerTranzPaymentStatus(await dispatch(getPowerTranzPaymentStatus({ attempt_ref: attemptRef })))
        if (isConfirmedAttemptState(status.state) || CONFIRMING_STATES.includes(status.state)) {
          goToResult(attemptRef)
        } else if (HOSTED_PAGE_STATES.includes(status.state)) {
          setInProgressUntil(status.expiresAt)
          setErrorCode(ERROR_CODES.PAYMENT_IN_PROGRESS)
        } else if (isFailedAttemptState(status.state)) {
          clearAttemptRef(storageKey)
        }
      } catch {
        // Unknown or tampered reference: start fresh.
        clearAttemptRef(storageKey)
      }
    },
    [storageKey, dispatch, goToResult]
  )

  useEffect(() => {
    const savedAttemptRef = getSavedAttemptRef(storageKey)
    if (savedAttemptRef) resolveExistingAttempt(savedAttemptRef)
  }, [storageKey, resolveExistingAttempt])

  // The live attempt's outcome, or null when the API can't say. Never treats "no answer" as failed.
  const fetchSessionStatus = useCallback(
    async (attemptRef) => {
      try {
        return mapPowerTranzPaymentStatus(await dispatch(getPowerTranzPaymentStatus({ attempt_ref: attemptRef })))
      } catch {
        return null
      }
    },
    [dispatch]
  )

  // A guest can be mid-3DS or have just submitted when the timer runs out, so ask before calling
  // the session expired.
  useEffect(() => {
    if (!isCheckingExpiry) return undefined
    let isActive = true
    fetchSessionStatus(attemptRef).then((status) => {
      if (!isActive) return
      setExpiryCheckedFor(attemptRef)
      if (isSettledOrConfirming(status)) goToResult(attemptRef)
    })
    return () => {
      isActive = false
    }
  }, [isCheckingExpiry, attemptRef, fetchSessionStatus, goToResult])

  // We can't see the popup's page, so notice when the payer closes it (the guide's form too: its
  // own polling only hears about a settled attempt, not an abandoned window).
  useEffect(() => {
    if (!isSessionLive || !isPopupMode) return undefined
    const timer = setInterval(() => {
      if (popupRef.current?.closed) setIsPopupClosed(true)
    }, POPUP_CLOSED_CHECK_MS)
    return () => clearInterval(timer)
  }, [isSessionLive, isPopupMode])

  // Closed after paying → the result; after a decline → say so and offer a new attempt; closed
  // before paying → PowerTranz's page can't be opened again (its token is single-use: a repost gets
  // 400), and the API refuses a new attempt until this one expires, so count down to that.
  const sessionExpiresAt = session?.expiresAt
  useEffect(() => {
    if (!isPopupClosed || !attemptRef) return undefined
    let isActive = true
    fetchSessionStatus(attemptRef).then((status) => {
      if (!isActive) return
      if (isSettledOrConfirming(status)) {
        goToResult(attemptRef)
      } else if (isFailedAttemptState(status?.state)) {
        clearAttemptRef(storageKey)
        setSession(null)
        setErrorCode(status.failureReason || status.state)
      } else if (HOSTED_PAGE_STATES.includes(status?.state)) {
        setSession(null)
        setInProgressUntil(sessionExpiresAt)
        setErrorCode(POPUP_CLOSED)
      }
    })
    return () => {
      isActive = false
    }
  }, [isPopupClosed, attemptRef, storageKey, sessionExpiresAt, fetchSessionStatus, goToResult])

  // The API's redirect page posts the result URL to the window that opened it — this page's
  // iframe parent, or the widget that opened the popup — then tries to navigate it, which browsers
  // often block across origins. Only our API, from our own frame or popup, may send it, and it may
  // only send us to this app (integration guide 5.3).
  useEffect(() => {
    if (!isHostedPageShown || (isPopupMode && !isWatchingPopup)) return undefined
    const handleResultMessage = (event) => {
      if (!API_ORIGIN || event.origin !== API_ORIGIN) return
      const expectedSource = isPopupMode ? popupRef.current : iframeRef.current?.contentWindow
      if (!expectedSource || event.source !== expectedSource) return
      const { type, url } = event.data || {}
      if (type !== RESULT_MESSAGE_TYPE || typeof url !== "string") return
      let target
      try {
        target = new URL(url)
      } catch {
        return
      }
      if (target.origin !== window.location.origin) return
      if (onResult) {
        onResult(getSavedAttemptRef(storageKey))
        return
      }
      navigate(target.hash.replace(/^#/, ""))
    }
    window.addEventListener("message", handleResultMessage)
    return () => window.removeEventListener("message", handleResultMessage)
  }, [isHostedPageShown, isPopupMode, isWatchingPopup, navigate, onResult, storageKey])

  const openPopup = () => {
    popupRef.current = window.open("", HOSTED_PAGE_POPUP_NAME, HOSTED_PAGE_POPUP_FEATURES)
    if (popupRef.current) setIsPopupClosed(false)
    return popupRef.current
  }

  const handleError = (error, popup) => {
    popup?.close()
    const code = getPowerTranzErrorCode(error)
    const attemptRef = error?.data?.attempt_ref
    if (PENDING_ERROR_CODES.includes(code)) {
      // A charge may already be in flight: never retry here — the result page polls for the truth.
      goToResult(attemptRef || getSavedAttemptRef(storageKey))
      return
    }
    if (code === ERROR_CODES.PAYMENT_IN_PROGRESS) {
      setInProgressUntil(error?.data?.expires_at || null)
      if (attemptRef) {
        saveAttemptRef(storageKey, attemptRef)
        resolveExistingAttempt(attemptRef)
      }
    }
    setPromoErrors(code === ERROR_CODES.INVALID_PROMO_CODE ? [].concat(error?.data?.message?.promo_codes || []) : [])
    setErrorCode(code || ERROR_CODES.GATEWAY_UNAVAILABLE)
  }

  const handleStartPayment = async () => {
    setErrorCode(null)
    setPromoErrors([])
    setInProgressUntil(null)
    // Popups must open synchronously in the click handler or browsers block them.
    const popup = isPopupMode ? openPopup() : null
    if (isPopupMode && !popup) {
      setErrorCode("popup_blocked")
      return
    }
    try {
      const response = await dispatch(
        createPowerTranzPayment({
          ...(bookingId && { booking_id: bookingId }),
          ...(promoCodes.length && { promo_codes: promoCodes }),
          ...(linkToken && { link_token: linkToken }),
          ...(saveCardOffer && { save_card_consent: isSaveCardConsented })
        })
      )
      const nextSession = mapPowerTranzPaymentSession(response)
      // A device clock running ahead would show a fresh session as already expired (no hosted page
      // at all); count the API's window from now instead. The API still enforces the real expiry.
      if (secondsUntil(nextSession.expiresAt) === 0) {
        nextSession.expiresAt = new Date(Date.now() + HOSTED_PAGE_SESSION_SECONDS * 1000).toISOString()
      }
      saveAttemptRef(storageKey, nextSession.attemptRef)
      saveConfirmationPath(storageKey, confirmationPath)
      setSession(nextSession)
      if (popup) writeToPopup(popup, nextSession.redirectData)
    } catch (error) {
      handleError(error, popup)
    }
  }

  const getErrorMessage = () => {
    if (errorCode === "popup_blocked") return t("powertranz.checkout.popup_blocked")
    // On a pay link the guest can't "refresh the booking": say the link is done with instead.
    if (linkToken && [ERROR_CODES.CHECKOUT_CLOSED, ERROR_CODES.ALREADY_PAID].includes(errorCode)) return t("powertranz.pay_link.closed")
    if (errorCode === POPUP_CLOSED) {
      return inProgressSecondsLeft > 0
        ? t("powertranz.checkout.popup_closed", { time: formatCountdown(inProgressSecondsLeft) })
        : t("powertranz.checkout.popup_closed_ready")
    }
    if (errorCode === ERROR_CODES.PAYMENT_IN_PROGRESS && inProgressSecondsLeft > 0) {
      return t("powertranz.checkout.in_progress", { time: formatCountdown(inProgressSecondsLeft) })
    }
    if (promoErrors.length) return promoErrors.join(" ")
    return getPowerTranzErrorMessage(t, errorCode)
  }

  const isWaitingOnOtherAttempt =
    [ERROR_CODES.PAYMENT_IN_PROGRESS, POPUP_CLOSED].includes(errorCode) && inProgressSecondsLeft > 0 && !isSessionLive
  const isStartDisabled = isDisabled || isCreating || isWaitingOnOtherAttempt
  const isFinalError = FINAL_CHECKOUT_ERROR_CODES.includes(errorCode)

  return (
    <Flex vertical gap={spacings.M}>
      {saveCardOffer && !isSessionLive && (
        <Checkbox checked={isSaveCardConsented} onChange={(event) => setIsSaveCardConsented(event.target.checked)}>
          {t("powertranz.checkout.save_card_consent", saveCardOffer)}
        </Checkbox>
      )}

      {errorCode && <Alert type="error" showIcon message={getErrorMessage()} />}

      {isSessionLive && (
        <Flex justify="space-between" wrap gap={spacings.XS}>
          {/* The API's amount, with the guide's processing fee when one applies (shown in the price summary). */}
          <Text strong>{t("powertranz.checkout.amount", { amount: numberToCurrency(session.amount, toCurrencyCode(session.currency)) })}</Text>
          <Text variant="caption">{t("powertranz.checkout.expires_in", { time: formatCountdown(sessionSecondsLeft) })}</Text>
        </Flex>
      )}
      {isSessionExpired && !isCheckingExpiry && <Alert type="warning" showIcon message={t("powertranz.errors.expired")} />}

      {isHostedPageShown && !isPopupMode && (
        <iframe
          ref={iframeRef}
          title={t("powertranz.checkout.frame_title")}
          className={styles.hostedPageFrame}
          srcDoc={session.redirectData}
          sandbox={HOSTED_PAGE_IFRAME_SANDBOX}
          allow="payment"
        />
      )}

      {isSessionLive && isPopupMode && (
        <Flex vertical gap={spacings.XS}>
          <Text>{t("powertranz.checkout.popup_open")}</Text>
        </Flex>
      )}

      {!isHostedPageShown && !isFinalError && (
        <Flex justify="center">
          <Button type="primary" onClick={handleStartPayment} loading={isCreating} disabled={isStartDisabled}>
            {isSessionExpired || errorCode ? t("powertranz.checkout.start_again") : t("powertranz.checkout.pay")}
          </Button>
        </Flex>
      )}
      <Text variant="caption">{t("powertranz.checkout.secure_note")}</Text>
    </Flex>
  )
}

PowerTranzCheckout.propTypes = {
  // Required unless paying from a link (linkToken), whose signed token names the booking.
  bookingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  promoCodes: PropTypes.arrayOf(PropTypes.string),
  // Signed payment-link token; set when paying from a guide-sent link.
  linkToken: PropTypes.string,
  // Present when the guest may save the card for the balance; values fill the consent sentence.
  saveCardOffer: PropTypes.shape({
    guideName: PropTypes.string,
    balanceAmount: PropTypes.string,
    dueDate: PropTypes.string
  }),
  isDisabled: PropTypes.bool,
  // Called with true while the hosted page is open, false otherwise.
  onSessionChange: PropTypes.func,
  // Called with the attempt reference when the outcome should be looked up, instead of navigating
  // to the result page.
  onResult: PropTypes.func,
  // Opens the hosted page in its own window and never lets its result page navigate this page.
  isStayingOnPage: PropTypes.bool,
  // Booking confirmation page the result page moves to once the payment is confirmed.
  confirmationPath: PropTypes.string
}

export default PowerTranzCheckout
